import React from "react";
import { useLocation, useParams } from "react-router-dom";
import { Flex, QueryLoader } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { useGetInvestmentEntitiesQuery } from "../../../../graphql/supergraphHooks";
import { toGlobalId } from "../../../../utils/graphql_utils";
import {
  ComplianceActivityCard,
  AUDIT_LOG_SUMMARY_PREVIEW_LIMIT,
} from "../../ComplianceActivityCard";
import { ComplianceStatusCard } from "../../ComplianceStatusCard";
import { PositionTable } from "../../ComplianceTable/PositionTable";
import { DashBoardHeader } from "../../DashBoardHeader";
import { DashboardTabs } from "../DashboardTabs";
import { activityCardContainer, cardsRow, statusCardContainer } from "../styles_vanilla.css";
import { useDashboardNavigation } from "../useDashboardNavigation";
import { useDashboardTab } from "../useDashboardTab";

type LocationState = { entityName?: string };

type Props = { arenaId: number; arenaName?: string };

export const EntityDashboard: React.FC<Props> = ({ arenaId, arenaName }) => {
  const [activeTab, setActiveTab] = useDashboardTab();
  const { state } = useLocation<LocationState>();
  const { eid } = useParams<{ eid: string }>();
  const entityId = Number(eid);
  const { navigateToArena, navigateToPosition } = useDashboardNavigation();

  const result = useGetInvestmentEntitiesQuery({
    fetchPolicy: "cache-and-network",
    variables: { arenaId, entities: [toGlobalId("AccountObjectType", entityId)] },
  });

  const entityName =
    state?.entityName ?? result.data?.investmentEntities?.edges?.[0]?.node?.name;

  return (
    <Flex flexDirection="column">
      <DashBoardHeader
        config={{
          variant: "entity",
          entityName: entityName,
          onRootClick: navigateToArena,
        }}
        tabs={<DashboardTabs activeTab={activeTab} onChange={setActiveTab} />}
      />

      <QueryLoader result={result}>
        <Flex flexDirection="column" gap={Sizes.LG}>
          {activeTab === "summary" && (
            <>
              <Flex className={cardsRow}>
                <Flex className={statusCardContainer}>
                  <ComplianceStatusCard
                    arenaId={arenaId}
                    entityId={entityId}
                    subtitle={entityName}
                  />
                </Flex>
                <Flex className={activityCardContainer}>
                  <ComplianceActivityCard
                    arenaId={arenaId}
                    arenaName={arenaName}
                    entityId={entityId}
                    maxVisibleItems={AUDIT_LOG_SUMMARY_PREVIEW_LIMIT}
                    onViewAll={() => setActiveTab("auditLog")}
                  />
                </Flex>
              </Flex>
              <PositionTable
                arenaId={arenaId}
                entityId={entityId}
                entityName={entityName}
                navigationCallback={(positionId: number) =>
                  navigateToPosition(positionId, entityId, entityName)
                }
              />
            </>
          )}
          {activeTab === "auditLog" && (
            <ComplianceActivityCard
              arenaId={arenaId}
              arenaName={arenaName}
              entityId={entityId}
            />
          )}
        </Flex>
      </QueryLoader>
    </Flex>
  );
};
